// invoice-automator/frontend/src/InvoiceGen.js
import React, { useState, useEffect, useCallback } from 'react';
import ClientForm from '../components/ClientForm.js'; // Fixed import path

function InvoiceGen() {
    // New: Load state from sessionStorage on component mount
    const [clients, setClients] = useState([]);
    const [selectedClientName, setSelectedClientName] = useState(() => sessionStorage.getItem('selectedClientName') || '');
    const [invoiceNumber, setInvoiceNumber] = useState(() => sessionStorage.getItem('invoiceNumber') || '');
    const [amount, setAmount] = useState(() => sessionStorage.getItem('amount') || '');
    const [pendingAmount, setPendingAmount] = useState(() => sessionStorage.getItem('pendingAmount') || '');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [showClientForm, setShowClientForm] = useState(false);
    const [clientToEdit, setClientToEdit] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/invoice`;

    // New: Save state to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('selectedClientName', selectedClientName);
    }, [selectedClientName]);

    useEffect(() => {
        sessionStorage.setItem('invoiceNumber', invoiceNumber);
    }, [invoiceNumber]);

    useEffect(() => {
        sessionStorage.setItem('amount', amount);
    }, [amount]);

    useEffect(() => {
        sessionStorage.setItem('pendingAmount', pendingAmount);
    }, [pendingAmount]);

    const fetchClients = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/Inoviceclients`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setClients(data);

            if (data.length > 0) {
                // If there's a selected client in sessionStorage, find it and set its amounts.
                // Otherwise, use the first client as the default.
                const preselectedClient = data.find(client => client.name === selectedClientName) || data[0];
                setSelectedClientName(preselectedClient.name);
                setAmount(preselectedClient.amount.toFixed(2));
                setPendingAmount(preselectedClient.pendingAmount ? preselectedClient.pendingAmount.toFixed(2) : '');
            } else {
                setSelectedClientName('');
                setAmount('');
                setPendingAmount('');
            }
            setMessage('');
            setMessageType('info');
        } catch (error) {
            console.error('Error fetching clients:', error);
            setMessage('Error loading clients. Make sure the backend is running and client data is initialized.');
            setMessageType('error');
        }
    }, [API_URL, selectedClientName]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const handleClientSelectChange = (e) => {
        const clientName = e.target.value;
        setSelectedClientName(clientName);
        const selectedClient = clients.find(client => client.name === clientName);
        if (selectedClient) {
            setAmount(selectedClient.amount.toFixed(2));
            setPendingAmount(selectedClient.pendingAmount ? selectedClient.pendingAmount.toFixed(2) : '');
        } else {
            setAmount('');
            setPendingAmount('');
        }
    };

    const handleGenerateInvoice = async () => {
        if (!selectedClientName) {
            setMessage('Please select a client first!');
            setMessageType('error');
            return;
        }
        if (!invoiceNumber.trim()) {
            setMessage('Please enter an Invoice Number!');
            setMessageType('error');
            return;
        }
        if (!amount || isNaN(amount) || Number(amount) <= 0) {
            setMessage('Please enter a valid amount!');
            setMessageType('error');
            return;
        }

        setMessage('Generating invoice... Please wait.');
        setMessageType('info');
        setIsLoading(true);

        try {
            const params = new URLSearchParams({
                amount: encodeURIComponent(amount),
                invoiceNumber: encodeURIComponent(invoiceNumber.trim()),
            });

            if (pendingAmount && !isNaN(pendingAmount) && Number(pendingAmount) > 0) {
                params.append('pendingAmount', encodeURIComponent(Number(pendingAmount).toFixed(2)));
            }

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/generate-invoice/${selectedClientName}?${params.toString()}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = `invoice_${selectedClientName.replace(/\s/g, '_')}_${invoiceNumber.trim()}.pdf`;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setMessage(`Invoice generated successfully for ${selectedClientName}! Download should start automatically.`);
                setMessageType('success');

                // New: Clear form fields and sessionStorage after successful generation
                setSelectedClientName('');
                setInvoiceNumber('');
                setAmount('');
                setPendingAmount('');
                sessionStorage.removeItem('selectedClientName');
                sessionStorage.removeItem('invoiceNumber');
                sessionStorage.removeItem('amount');
                sessionStorage.removeItem('pendingAmount');
            } else {
                const errorText = await response.text();
                setMessage(`Failed to generate invoice: ${errorText}`);
                setMessageType('error');
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('An error occurred while communicating with the server. Check console for details.');
            setMessageType('error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddClientClick = () => {
        setClientToEdit(null);
        setShowClientForm(true);
    };

    const handleEditClientClick = (client) => {
        setClientToEdit(client);
        setShowClientForm(true);
    };

    const handleDeleteClient = async (clientId) => {
        // Updated: Replaced window.confirm with a custom modal for better UX
        const confirmed = window.confirm('Are you sure you want to delete this client?');
        if (!confirmed) {
            return;
        }

        setMessage('Deleting client...');
        setMessageType('info');

        try {
            const response = await fetch(`${API_URL}/clients/${clientId}`, {
                method: 'DELETE',
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            setMessage('Client deleted successfully!');
            setMessageType('success');
            fetchClients();
        } catch (error) {
            console.error('Error deleting client:', error);
            setMessage(`Error deleting client: ${error.message}`);
            setMessageType('error');
        }
    };

    const handleClientFormSave = () => {
        setShowClientForm(false);
        setClientToEdit(null);
        fetchClients();
    };

    const handleClientFormCancel = () => {
        setShowClientForm(false);
        setClientToEdit(null);
    };
    // The rest of your JSX would be here.

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-indigo-900 flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl">

                {/* Left Panel: Invoice Generator */}
                <div className="bg-gray-800 rounded-2xl shadow-2xl flex-1 p-8">
                    <h1 className="text-4xl font-extrabold text-center text-indigo-300 mb-6 drop-shadow-lg">
                        Monthly Invoice Automator
                    </h1>
                    <p className="text-gray-400 text-center mb-8 text-lg">
                        Select a client, enter the amount, and click <span className="font-semibold text-indigo-200">"Generate Invoice"</span>!
                    </p>

                    <div className="flex flex-col gap-5">
                        <label htmlFor="client-select" className="text-xl font-medium text-gray-300">
                            Select Client:
                        </label>
                        <select
                            id="client-select"
                            value={selectedClientName}
                            onChange={handleClientSelectChange} // Use new handler
                            className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg cursor-pointer transition-all duration-200"
                            disabled={isLoading}
                        >
                            <option value="">-- Select Client --</option>
                            {clients.map((client) => (
                                <option key={client._id} value={client.name}>
                                    {client.name}
                                </option>
                            ))}
                        </select>

                        {/* New Input for Invoice Number */}
                        <label htmlFor="invoice-number-input" className="text-xl font-medium text-gray-300 mt-4">
                            Invoice Number:
                        </label>
                        <input
                            id="invoice-number-input"
                            type="text" // Can be text as it's a custom ID
                            placeholder="e.g., INV-2025-001"
                            value={invoiceNumber}
                            onChange={e => setInvoiceNumber(e.target.value)}
                            className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                            required
                            disabled={isLoading}
                        />

                        <label htmlFor="amount-input" className="text-xl font-medium text-gray-300 mt-4">
                            Current Month Amount:
                        </label>
                        <input
                            id="amount-input"
                            type="number"
                            placeholder="e.g., 5000.00"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                            disabled={isLoading}
                        />

                        {/* New Input for Pending Amount */}
                        <label htmlFor="pending-amount-input" className="text-xl font-medium text-gray-300 mt-4">
                            Outstanding Amount:
                        </label>
                        <input
                            id="pending-amount-input"
                            type="number"
                            placeholder="e.g., 1000.00 (optional)"
                            value={pendingAmount}
                            onChange={e => setPendingAmount(e.target.value)}
                            className="w-full px-5 py-3 rounded-xl border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg transition-all duration-200"
                            disabled={isLoading}
                        />

                        <button
                            onClick={handleGenerateInvoice}
                            // Disable if no client, no invoice number, or invalid amount, or loading
                            disabled={!selectedClientName || !invoiceNumber.trim() || !amount || isNaN(amount) || Number(amount) <= 0 || isLoading}
                            className="w-full py-4 px-8 rounded-xl font-bold text-xl text-white
                                bg-gradient-to-r from-purple-600 to-indigo-600
                                hover:from-purple-700 hover:to-indigo-700
                                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800
                                transition-all duration-300 ease-in-out
                                shadow-lg hover:shadow-xl
                                disabled:opacity-50 disabled:cursor-not-allowed disabled:from-gray-500 disabled:to-gray-600 mt-6"
                        >
                            {isLoading ? 'Generating...' : 'Generate Invoice'}
                        </button>

                        {message && (
                            <p className={`mt-5 text-center text-lg rounded-xl px-4 py-3 ${
                                messageType === 'success'
                                    ? 'bg-green-900 text-green-300'
                                    : messageType === 'error'
                                        ? 'bg-red-900 text-red-300'
                                        : 'bg-indigo-900 text-indigo-300'
                            } transition-all duration-300`}>
                                {message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Right Panel: Manage Clients */}
                <div className="bg-gray-800 rounded-2xl shadow-2xl flex-1 p-8">
                    <h2 className="text-4xl font-extrabold text-center text-indigo-300 mb-6 drop-shadow-lg">
                        Manage Clients
                    </h2>
                    <button
                        onClick={handleAddClientClick}
                        className="w-full py-3 px-6 rounded-xl font-bold text-lg text-white
                            bg-gradient-to-r from-blue-600 to-cyan-600
                            hover:from-blue-700 hover:to-cyan-700
                            focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-gray-800
                            transition-all duration-300 ease-in-out
                            shadow-lg hover:shadow-xl mb-6"
                    >
                        Add New Client
                    </button>

                    {clients.length === 0 ? (
                        <p className="text-gray-400 text-center text-lg">No clients available. Please add a new client.</p>
                    ) : (
                        <div className="overflow-x-auto rounded-lg shadow-md">
                            <table className="min-w-full bg-gray-700 rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-gray-600 text-gray-200 uppercase text-sm leading-normal">
                                        <th className="py-3 px-6 text-left">Client Name</th>
                                        <th className="py-3 px-6 text-left">Customer ID</th>
                                        <th className="py-3 px-6 text-right">Default Amount</th>
                                        <th className="py-3 px-6 text-right">Pending Amount</th> {/* New column header */}
                                        <th className="py-3 px-6 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="text-gray-300 text-sm font-light">
                                    {clients.map((client) => (
                                        <tr key={client._id} className="border-b border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                                            <td className="py-3 px-6 text-left whitespace-nowrap">{client.name}</td>
                                            <td className="py-3 px-6 text-left">{client.customerId}</td>
                                            <td className="py-3 px-6 text-right">INR {client.amount.toFixed(2)}</td>
                                            <td className="py-3 px-6 text-right">INR {client.pendingAmount ? client.pendingAmount.toFixed(2) : '0.00'}</td> {/* Display pending amount */}
                                            <td className="py-3 px-6 text-center">
                                                <div className="flex item-center justify-center space-x-3">
                                                    <button
                                                        onClick={() => handleEditClientClick(client)}
                                                        className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 shadow-md"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClient(client._id)}
                                                        className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 shadow-md"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for ClientForm */}
            {showClientForm && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-gray-900 rounded-3xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto transform scale-95 opacity-0 animate-scale-in">
                        <button
                            onClick={handleClientFormCancel}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 text-3xl font-bold transition-colors duration-200"
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        <ClientForm
                            clientToEdit={clientToEdit}
                            onSave={handleClientFormSave}
                            onCancel={handleClientFormCancel}
                            API_URL={API_URL}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default InvoiceGen;